import { useState, useRef, useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { ResizedImg } from "./img";
import ListingStatus from "./listing-status";
import PercentOffBadge from "./percent-off-badge";
import ListingCopiesBadge from "./listing-copies-badge";
import ListingThumbCorner from "./listing-thumb-corner";
import { getConditionLabel } from "../generated/static-categories";
import { useRouterContext } from '../contexts/RouterProvider';
import { prefetchProductUrl } from '../utils/prefetch-product';
import AdminContextMenu from './admin-context-menu';
import { useAdminListingMenu } from '../hooks/useAdminListingMenu';
import { useListingHover } from '../utils/useListingHover';
import { rememberListingOrigin } from '../utils/listing-click-origin';
import { useListingImpression } from '../hooks/useListingImpression';
import { recordInterest } from '../utils/session-interest';
import { useListingSlideshow } from '../utils/useListingSlideshow';
import { useListingThumbApi } from './useListingThumbApi';
import { useIsMobile } from '../hooks/useIsMobile';
import type { ListingPageType } from '../utils/listing-page-type';

const WishlistButton = dynamic(
	() => import('./wishlist-button'),
	{ ssr: false }
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PhotoItem = Record<string, any>;

// Helper function to extract second photo URL
const getSecondPhotoUrl = (photos: PhotoItem[]): string => {
	// Both callers only ask once photos.length > 1.
	const secondPhoto = photos[1];
	if (typeof secondPhoto === 'object' && secondPhoto !== null) {
		return secondPhoto['320x320Url'] || secondPhoto.url || secondPhoto.thumbUrl || '';
	}
	return typeof secondPhoto === 'string' ? secondPhoto : '';
};

interface ListingThumbProps {
	url: string;
	title: string;
	outgoing?: Record<string, unknown>;
	trustLevel?: number;
	amount?: React.ReactNode;
	status?: number;
	photo?: string;
	isJob?: boolean;
	local?: boolean;
	id: string | number;
	slashedPrice?: string;
	clickable?: boolean;
	percentOff?: number;
	itemCondition?: string | number;
	dataSourceId?: number;
	tag?: string;
	categoryId?: string | number;
	categoryGroupId?: string | number;
	// Hover tracking props
	resultPosition?: number;
	totalResults?: number;
	searchQuery?: string;
	pageType?: ListingPageType;
	activeFilters?: Record<string, string>;
	tags?: string[];
	/** Which rendered row this card sits in, e.g. 'trending-now'. */
	listName?: string;
	/** 1-indexed position of that row on the page. */
	rowIndex?: number;
	photos?: PhotoItem[];
	hasPhoto?: number;
	featuredDate?: Date | string | null;
	/** Live listings this seller has of this same item, this one included. */
	copies?: number;
	/** "City, ST", for the thumb-location UI test. */
	locationText?: string | null;
}

const ListingThumb = ({
	url,
	title,
	outgoing,
	trustLevel = 0,
	amount,
	status = 0,
	photo,
	isJob = false,
	local: _local = false,
	id,
	slashedPrice,
	clickable = true,
	percentOff,
	itemCondition,
	dataSourceId,
	tag: _tag,
	categoryId,
	categoryGroupId,
	// Hover tracking props
	resultPosition,
	totalResults,
	searchQuery,
	pageType,
	activeFilters,
	tags,
	listName,
	rowIndex,
	photos,
	hasPhoto = 0,
	featuredDate,
	copies,
	locationText,
}: ListingThumbProps) => {
	const { prefetch } = useRouterContext();
	const {
		isFeatureLoading,
		isPartnerLoading,
		isArchiveLoading,
		handleFeature,
		handleFeatureForPartner,
		handleArchive,
		isNsfwLoading,
		handleNsfw,
		loadNsfwState,
		nsfwFor,
	} = useListingThumbApi();
	const [isFlipped, setIsFlipped] = useState(false);
	const isMobile = useIsMobile();

	// Check if listing is featured
	const isFeatured = !!featuredDate;

	// Handle hover for flip effect
	const handleFlipHover = () => {
		if (!isMobile && photos && photos.length > 1) {
			setIsFlipped(true);
		}
	};

	// Slideshow for listings with 3+ photos
	const {
		currentIndex,
		allPhotos,
		isSlideshow,
		handleImageError,
		startSlideshow,
		stopSlideshow,
		hasImageFailed
	} = useListingSlideshow({
		listingId: typeof id === 'string' ? parseInt(id) : id,
		initialPhotos: useMemo(() => (photos || []) as { url: string }[], [photos]),
		hasPhoto: hasPhoto || 0,
		dataSourceId,
	});

	// Preload 2nd image when listing comes into view
	const listingRef = useRef<HTMLDivElement | HTMLAnchorElement>(null);
	const slideshowTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Admin-only right-click menu, positioned against this tile.
	const {
		isAdmin,
		menu: contextMenu,
		openMenu: openContextMenu,
		closeMenu: closeContextMenu,
	} = useAdminListingMenu(listingRef as React.RefObject<HTMLElement | null>);

	useEffect(() => {
		if (isMobile) return;
		if (!photos || photos.length < 2) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const secondPhotoUrl = getSecondPhotoUrl(photos);
						if (secondPhotoUrl) {
							const img = new Image();
							img.src = secondPhotoUrl;
						}
						observer.disconnect();
					}
				});
			},
			{ rootMargin: '50px' }
		);

		// The tile is always mounted by the time an effect runs, so the ref is set.
		observer.observe(listingRef.current as Element);

		return () => observer.disconnect();
	}, [photos, isMobile]);

	// Hover tracking
	const numericId = typeof id === 'string' ? parseInt(id) : id;
	const hoverHandlers = useListingHover({
		listingId: numericId,
		listingTitle: title,
		listingPrice: typeof amount === 'number' ? amount : 0,
		categoryId: categoryId ? (typeof categoryId === 'string' ? parseInt(categoryId) : categoryId) : undefined,
		categoryGroupId: categoryGroupId ? (typeof categoryGroupId === 'string' ? parseInt(categoryGroupId) : categoryGroupId) : undefined,
		resultPosition,
		totalResults,
		searchQuery,
		pageType,
		activeFilters,
		tags,
		listName,
		rowIndex,
	});

	// One impression when the card is genuinely seen -- half visible for a
	// continuous dwell, so a fast scroll-past does not count and deflate the rate.
	// Does nothing at all for an unsampled session, which is most of them.
	useListingImpression(
		listingRef as React.RefObject<Element | null>,
		resultPosition === undefined
			? null
			: {
					listing_id: numericId,
					list_name: listName,
					result_position: resultPosition,
					row_index: rowIndex,
					page_type: pageType,
					total_results: totalResults,
					tags,
					category_id: categoryId ? Number(categoryId) : undefined,
					category_group_id: categoryGroupId ? Number(categoryGroupId) : undefined,
					data_source_id: dataSourceId,
				}
	);

	// Record where this click happened, at the only moment the list context
	// exists. TrackListingView reads it back on the product page -- listing_view
	// fires after navigation, so without this the position is simply gone.
	// onClickCapture, so it runs even if a child stops propagation.
	const rememberOrigin = () => {
		// A click is the strongest browsing signal there is, so it feeds the
		// session interest vector as well as the click-origin record.
		recordInterest({ signal: 'view', tags, categoryGroupId });
		rememberListingOrigin(numericId, {
			resultPosition,
			totalResults,
			listName,
			rowIndex,
			pageType,
			searchQuery,
			activeFilters,
			tags,
		});
	};

	const Wrapper = clickable ? 'a' : 'div';
	const wrapperProps = clickable ? { href: url, ...outgoing } : {};

	return (
		<>
			<Wrapper
				ref={listingRef as React.Ref<HTMLAnchorElement & HTMLDivElement>}
				className={`listing ${isJob ? "job" : ""} ${!clickable ? "pointer-events-none" : ""} group ${contextMenu.visible ? "listing--highlighted" : ""}`}
				data-testid={`listing-card-${id}`}
				{...wrapperProps}
				onClickCapture={rememberOrigin}
				onAuxClick={rememberOrigin}
				onContextMenu={openContextMenu}
				onMouseEnter={() => {
					if (isMobile) return;
					// Warm the product route (JS + ISR data) on hover intent so the
					// click lands on an already-generated page. Deduped + guarded to
					// internal /product/ urls inside the helper.
					if (clickable && !isJob) {
						prefetchProductUrl(url, prefetch);
					}
					// Flip to second image
					handleFlipHover();
					// Analytics tracking has its own 500ms debounce
					if (hoverHandlers.onMouseEnter) {
						hoverHandlers.onMouseEnter();
					}
					// Start slideshow after flip completes (if hasPhoto >= 3 and not already active)
					if (hasPhoto >= 3 && !isSlideshow) {
						slideshowTimeoutRef.current = setTimeout(() => {
							startSlideshow();
						}, 450);
					}
				}}
				onMouseLeave={() => {
					// Cancel pending slideshow start
					if (slideshowTimeoutRef.current) {
						clearTimeout(slideshowTimeoutRef.current);
						slideshowTimeoutRef.current = null;
					}
					// Stop slideshow and reset
					stopSlideshow();
					// Flip back immediately with CSS transition
					setIsFlipped(false);
					// Call analytics leave handler
					if (hoverHandlers.onMouseLeave) {
						hoverHandlers.onMouseLeave();
					}
				}}
			>
				<div className="listing-image relative" data-testid="listing-image">
				<div className="listing-image-cont skeleton">
					{photo && (
						!isMobile && photos && photos.length > 1 ? (
							// Multi-image with slideshow support
							<div className="w-full h-full relative">
								{/* Photo 2 - always rendered underneath */}
								<ResizedImg
									src={getSecondPhotoUrl(photos)}
									size={320}
									alt={`Photo of ${title || ""}`}
									className="w-full h-full object-cover absolute top-0 left-0"
									dataSourceId={dataSourceId}
									loading="lazy"
								/>
								{/* Slideshow images - all rendered, crossfade via opacity */}
								{isSlideshow && allPhotos.map((slideshowPhoto, index) => {
									if (hasImageFailed(slideshowPhoto)) return null;
									return (
										<ResizedImg
											// Index first: a listing can carry the same photo URL twice,
											// and a duplicate key makes React duplicate or omit siblings.
											key={`slide-${typeof id === 'string' ? id : id.toString()}-${index}-${slideshowPhoto?.['320x320Url'] || ''}`}
											src={slideshowPhoto?.['320x320Url'] || slideshowPhoto?.url}
											size={320}
											alt={`Photo ${index + 1} of ${title || ""}`}
											className={`w-full h-full object-cover absolute top-0 left-0 listing-image-curtain ${currentIndex === index ? 'opacity-100' : 'opacity-0'}`}
											dataSourceId={dataSourceId}
											onError={() => handleImageError(slideshowPhoto?.['320x320Url'] || slideshowPhoto?.url)}
										/>
									);
								})}
								{/* Photo 1 curtain - fades out on hover/slideshow.
								    Through ResizedImg like every other layer here: this is the
								    one a shopper actually sees at rest, so a raw <img> made the
								    most-viewed image on the site the only one without AVIF,
								    while the hidden layer underneath it had one. */}
								<ResizedImg
									src={photo}
									size={320}
									alt={`Photo of ${title || ""}`}
									className={`w-full h-full object-cover absolute top-0 left-0 listing-image-curtain pointer-events-none ${isFlipped || isSlideshow ? 'opacity-0' : 'opacity-100'}`}
									dataSourceId={dataSourceId}
									loading="lazy"
								/>
							</div>
						) : (
							// Single image
							<ResizedImg
								src={photo}
								size={320}
								alt={`Photo of ${title || ""}`}
								className="swiper-lazy w-full h-full object-cover absolute top-0 left-0"
								dataSourceId={dataSourceId}
								loading="lazy"
							/>
						)
					)}
				</div>
				{percentOff && status < 4 ? (
					<PercentOffBadge percentOff={percentOff} testId="listing-percent-off" />
				) : (
					<ListingStatus status={status} />
				)}
				<ListingCopiesBadge copies={copies} />
				{!!itemCondition && (
					<div className="absolute bottom-2 right-2 text-md font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded" data-testid="listing-condition">
						{getConditionLabel(typeof itemCondition === 'string' ? parseInt(itemCondition) : itemCondition)}
					</div>
				)}

				{/* Wishlist Button - positioned bottom-left */}
				<div className="absolute bottom-2 left-2 z-10" data-testid="listing-wishlist">
					<WishlistButton listingId={numericId} />
				</div>
			</div>
			<div className="listing-price">
				<div className="title" data-testid="listing-title">{title}</div>
				<div className="thumb-price-row">
					{slashedPrice ? (
						<div className="flex items-center gap-3">
							<span className="line-through text-gray-400 text-[13px] opacity-80" data-testid="listing-slashed-price">
								{slashedPrice}
							</span>
							<div className="thumb-price" data-testid="listing-price">
								{amount}
							</div>
						</div>
					) : (
						<div className="thumb-price" data-testid="listing-price">
							{amount}
						</div>
					)}
					{/* Verified badge or location: the thumb-location UI test */}
					<ListingThumbCorner id={id} trustLevel={trustLevel} locationText={locationText} />
				</div>
			</div>
		</Wrapper>
		{isAdmin && (
			<AdminContextMenu
				isVisible={contextMenu.visible}
				position={{ x: contextMenu.x, y: contextMenu.y }}
				listingRect={contextMenu.listingRect}
				listingId={numericId}
				isFeatured={isFeatured}
				onFeature={handleFeature}
				onFeatureForPartner={handleFeatureForPartner}
				onArchive={handleArchive}
				onNsfw={handleNsfw}
				onLoadNsfw={loadNsfwState}
				isNsfw={nsfwFor(numericId)}
				onClose={closeContextMenu}
				isFeatureLoading={isFeatureLoading}
				isPartnerLoading={isPartnerLoading}
				isArchiveLoading={isArchiveLoading}
				isNsfwLoading={isNsfwLoading}
			/>
		)}
		</>
	);
};

export default ListingThumb;
